import React, { useCallback, useEffect, useState } from 'react';
import { prop } from 'ramda';
import { useGetPlayerTags } from 'server';

import { BackboneReplacer } from '@core/js/backbone/components/BackboneReplacer';
import { Icon } from '@core/js/components/common/Icon';
import { Link } from '@core/js/components/common/Link';

import { useOpenChat, useOpenInbox } from '@core/js/components/system/CommunicationWidgets/hooks';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import {
    useGetAvailableMenuItems
} from '@interlayer/js/custom/components/main/NavigationBar/hooks/useGetAvailableMenuItems';
import { CUSTOM_CSS_CLASSES } from '@interlayer/js/custom/constants';
import { useDynamicTags } from '@interlayer/js/custom/hooks/useDynamicTags';
import { useCheckIsActiveLink } from '@interlayer/js/custom/hooks/useMenu';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/custom/utils/constants';

import style from './NavigationBar.scss';

export const NavigationBar = ({ currentPageURL }) => {
    const checkIsActiveLink = useCheckIsActiveLink(currentPageURL);
    const currentMenuItems = useGetAvailableMenuItems(currentPageURL);
    const isAdminLoggedIn = useAppSelector(prop('isAdminLoggedIn'));
    const [playerTags, setPlayerTags] = useState([]);
    const [dTagSafebet] = useDynamicTags([DYNAMIC_TAG_NAMES.SAFEBET]);
    const isHidden = (item) => item.cssClasses.includes(CUSTOM_CSS_CLASSES.HIDE_PAGE_WITHOUT_SAFEBET_TAG)
        && playerTags.length === 0
        && !isAdminLoggedIn;
    const hasActivePage = (pages) => pages?.some((page) => checkIsActiveLink(page.url));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const getPlayerTags = useGetPlayerTags();

    const fetchPlayerTags = useCallback(async () => {
        const { tags } = await getPlayerTags({
            tagName: dTagSafebet
        });

        setPlayerTags(tags || []);
    }, [dTagSafebet, getPlayerTags]);

    useEffect(() => {
        if (isLoggedIn) {
            (async () => fetchPlayerTags())();
        } else {
            setPlayerTags([]);
        }

    }, [fetchPlayerTags, isLoggedIn]);

    const onOpenInbox = useOpenInbox();
    const onOpenChat = useOpenChat();

    if (!currentMenuItems.length) {
        return null;
    }

    return (
        <div className={`${style.container} cms-navigation-bar`}>
            <ul className={style.list}>
                {currentMenuItems.map(item => (
                    <li key={item.url} className={`${style.listItem_wrapper}`} >
                        {item.inboxOpener && (
                            <div className={`${style.listItem} ${checkIsActiveLink(item.url) ? `${style.isActive} active` : ''} ${item.cssClasses ? `${item.cssClasses} ` : ''}`}>
                                <a
                                    href='#'
                                    className={`${style.listItemPoint} ${style.listItemPointReplacer}`}
                                    onClick={onOpenInbox}>
                                    <div className={style.replacerContainer}>
                                        <span className={`micon-${item.menuIcon ? item.menuIcon : ''}`} />
                                        <span className={`${style.itemName} ${style.replacerItem}`}>{item.name}</span>
                                        <BackboneReplacer dynamicName={'UNREAD_MESSAGES_COUNT'} viewName={'unreadmessagescount'} />
                                    </div>
                                </a>
                            </div>
                        )}
                        {item.chatOpener && (
                            <div className={`${style.listItem} ${item.cssClasses ? `${item.cssClasses} ` : ''} ${checkIsActiveLink(item.url) ? `${style.isActive} active` : ''}`}>
                                <a
                                    href='#'
                                    className={`${style.listItemPoint} ${style.listItemPointReplacer}`}
                                    onClick={onOpenChat}>
                                    <div className={style.replacerContainer}>
                                        <span className={`micon-${item.menuIcon ? item.menuIcon : ''}`} />
                                        <span className={`${style.itemName} ${style.replacerItem}`}>{item.name}</span>
                                        <BackboneReplacer dynamicName={'CHAT_UNREAD'} viewName={'chatunread'} />
                                    </div>
                                </a>
                            </div>
                        )}
                        {!item.inboxOpener && !item.chatOpener && (
                            <div
                                className={`${isHidden(item) ? style.hidden : ''} 
                                ${checkIsActiveLink(item.url) && !item.children ? `${style.isActive} active` : ''} 
                                ${style.listItem} ${item.cssClasses ? `${item.cssClasses} ` : ''}`}
                                data-url={item.url}
                            >
                                <Link
                                    href={item.url}
                                    inNewWindow={item.target === '_blank'}
                                    className={`${style.link} ${style.listItemPoint} ${item.children ? style.dropdown : ''}`}
                                >
                                    <span className={`micon-${item.menuIcon ? item.menuIcon : ''}`} />
                                    <span className={style.itemName}>{item.name}</span>
                                    {item.children && (
                                        <Icon
                                            type={hasActivePage(item.children) ? 'topArrow' : 'bottomArrow'}
                                            className={style.dropdownIcon}
                                        />
                                    )}
                                </Link>
                                {item.children && (
                                    <ul
                                        className={`${style.subMenu} ${hasActivePage(item.children) ? `${style.isActive}` : ''}` }
                                    >
                                        {item.children.map((subItem) => (
                                            <li
                                                className={`
                                                    ${style.subMenuItem}  
                                                    ${checkIsActiveLink(subItem.url) ? style.isActive : ''}
                                                    ${isHidden(subItem) ? style.hidden : ''}
                                                `}
                                                key={subItem.url}>
                                                <Link
                                                    href={subItem.url}
                                                    inNewWindow={subItem.target === '_blank'}
                                                    className={`${style.subMenuItemLink} ${style.listItemPoint}`}
                                                >
                                                    <span className={`micon-${subItem.menuIcon ? subItem.menuIcon : ''}`} />
                                                    <span className={style.itemName}>{subItem.name}</span>
                                                </Link>
                                            </li>
                                        ))
                                        }
                                    </ul>
                                )}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};
